import { styled } from '@mui/material/styles';
import type { Virtualizer } from '@tanstack/react-virtual';
import { useContext } from 'react';
import {
  ColRowHighlightContext,
  useExportMode,
  useSwimlaneRootProps,
  useSwimlaneSlot,
} from '@/components/ui/swimlane/context';
import { getCellWidth } from '@/components/ui/swimlane/layout/body/cards/CardUtils';
import {
  COL_ROW_HIGHLIGHT_COLOR,
  COLUMN_HEADER_HEIGHT,
  GRID_GAP,
} from '@/components/ui/swimlane/layout/constants';

interface IColumnHeaders {
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

export function ColumnHeaders({ columnVirtualizer }: IColumnHeaders) {
  const { component: SlotColumnHeader, props: columnHeaderSlotProps } =
    useSwimlaneSlot('columnHeader');
  const { columnAxisLabelText, columnHeaders, isCompact, isHeadersSticky, backgroundColor } =
    useSwimlaneRootProps();
  const { isExporting } = useExportMode();
  const { colHighlightIndex, setColHighlightIndex } = useContext(ColRowHighlightContext);

  const effectiveIsHeadersSticky = isHeadersSticky && !isExporting;

  const hasAxisLabelColumn = typeof columnAxisLabelText === 'string' && effectiveIsHeadersSticky;

  return (
    <Container
      data-export="column-header-container"
      isHeadersSticky={effectiveIsHeadersSticky}
      backgroundColor={backgroundColor}
      style={{
        gridArea: 'colH',
        width: `${columnVirtualizer.getTotalSize()}px`,
        top: `${hasAxisLabelColumn ? `${COLUMN_HEADER_HEIGHT.value + GRID_GAP.value}px` : '0px'}`,
      }}
    >
      {columnVirtualizer.getVirtualItems().map((virtualColumn) => {
        const columnHeaderData = columnHeaders[virtualColumn.index];
        const isColumnHighlighted = colHighlightIndex === virtualColumn.index;
        // styles for highlighting column header
        const columnHighLightStyles = {
          backgroundColor: COL_ROW_HIGHLIGHT_COLOR,
          outline: `6px solid ${COL_ROW_HIGHLIGHT_COLOR}`,
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
        };

        return (
          <ColumnHeaderContainer
            key={virtualColumn.key}
            style={{
              width: `${getCellWidth(isCompact)}px`,
              transform: `translateX(${virtualColumn.start}px)`,
              ...(isColumnHighlighted ? columnHighLightStyles : {}),
              backgroundColor,
            }}
            onMouseEnter={() => setColHighlightIndex(virtualColumn.index)}
            onMouseLeave={() => setColHighlightIndex(-1)}
          >
            {columnHeaderData && (
              <SlotColumnHeader
                {...columnHeaderSlotProps}
                {...columnHeaderData}
                id={String(columnHeaders[virtualColumn.index]?.id)}
              />
            )}
          </ColumnHeaderContainer>
        );
      })}
    </Container>
  );
}

const ColumnHeaderContainer = styled('div')({
  position: 'absolute',
  height: COLUMN_HEADER_HEIGHT.label,
});

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isHeadersSticky' && prop !== 'backgroundColor',
})<{ isHeadersSticky: boolean; backgroundColor?: string }>(
  ({ isHeadersSticky, backgroundColor }) => ({
    backgroundColor: backgroundColor || 'var(--color-bg-surface-base)',
    position: isHeadersSticky ? 'sticky' : 'relative',
    zIndex: 10,
  }),
);
